import { Box } from "@mui/material";

import Images from "./ProductBuilderImage.utils";

type Props = {
    qcomHwChecked: boolean;
    nxpHwChecked: boolean;
    customHwChecked: boolean;
    headlessChecked: boolean;
    kioskChecked: boolean;
    customUiChecked: boolean;
    cloudChecked: boolean;
    fleetChecked: boolean;
    fleetAnalyticsChecked: boolean;
};

type HardwareVariants = {
    screenOffKioskOff: string;
    screenOffKioskOn: string;
    screenOffCustomizeOn: string;
    screenOn: string;
    kioskOn: string;
    screenOnCustomizeOn: string;
};

const hardwareVariants = {
    nxp: {
        screenOffKioskOff: Images.hardware.nxpScreenOffKioskOff,
        screenOffKioskOn: Images.hardware.nxpScreenOffKioskOn,
        screenOffCustomizeOn: Images.hardware.nxpScreenOffCustomizeOn,
        screenOn: Images.hardware.nxpScreenOn,
        kioskOn: Images.hardware.nxpKioskOn,
        screenOnCustomizeOn: Images.hardware.nxpScreenOnCustomizeOn,
    },
    qualcomm: {
        screenOffKioskOff: Images.hardware.qualcommScreenOffKioskOff,
        screenOffKioskOn: Images.hardware.qualcommScreenOffKioskOn,
        screenOffCustomizeOn: Images.hardware.qualcommScreenOffCustomizeOn,
        screenOn: Images.hardware.qualcommScreenOn,
        kioskOn: Images.hardware.qualcommKioskOn,
        screenOnCustomizeOn: Images.hardware.qualcommScreenOnCustomizeOn,
    },
    customhw: {
        screenOffKioskOff: Images.hardware.customhwScreenOffKioskOff,
        screenOffKioskOn: Images.hardware.customhwScreenOffKioskOn,
        screenOffCustomizeOn: Images.hardware.customhwScreenOffCustomizeOn,
        screenOn: Images.hardware.customhwScreenOn,
        kioskOn: Images.hardware.customhwKioskOn,
        screenOnCustomizeOn: Images.hardware.customhwScreenOnCustomizeOn,
    },
} satisfies Record<string, HardwareVariants>;

const pickHardwareVariants = (props: Props): HardwareVariants | null => {
    // More than one board can be checked at once; the last one listed here wins, as it did when each board
    // overwrote the previous one's image.
    if (props.customHwChecked) {
        return hardwareVariants.customhw;
    }
    if (props.qcomHwChecked) {
        return hardwareVariants.qualcomm;
    }
    if (props.nxpHwChecked) {
        return hardwareVariants.nxp;
    }
    return null;
};

const getHardwareImage = (props: Props): string => {
    const variants = pickHardwareVariants(props);
    if (!variants) {
        return "";
    }

    if (props.headlessChecked) {
        if (props.kioskChecked) {
            return variants.screenOffKioskOn;
        }
        return props.customUiChecked ? variants.screenOffCustomizeOn : variants.screenOffKioskOff;
    }

    if (props.kioskChecked) {
        return variants.kioskOn;
    }
    return props.customUiChecked ? variants.screenOnCustomizeOn : variants.screenOn;
};

const getServiceImage = ({ cloudChecked, fleetChecked, fleetAnalyticsChecked }: Props): string => {
    if (cloudChecked && fleetChecked && fleetAnalyticsChecked) {
        return Images.services.allServices;
    }
    if (fleetChecked && fleetAnalyticsChecked) {
        return Images.services.fleetAndFleetAnalytics;
    }
    if (cloudChecked && fleetAnalyticsChecked) {
        return Images.services.dataAndFleetAnalytics;
    }
    if (cloudChecked && fleetChecked) {
        return Images.services.dataAndFleetManagement;
    }
    if (fleetAnalyticsChecked) {
        return Images.services.fleetAnalytics;
    }
    if (fleetChecked) {
        return Images.services.fleetManagement;
    }
    return cloudChecked ? Images.services.dataManagement : "";
};

export const ProductBuilderImage = (props: Props) => {
    const hardwareImage = getHardwareImage(props);
    const serviceImage = getServiceImage(props);

    return (
        <Box
            data-testid="product-builder-image"
            sx={{
                height: "120%",
                width: "120%",
                backgroundImage: `url(${hardwareImage}), url(${serviceImage})`,
                backgroundSize: "contain, contain",
                backgroundPosition: "center, center",
                backgroundRepeat: "no-repeat, no-repeat",
            }}
        />
    );
};
